import { type ReactNode } from 'react'
import { ListTodo, SearchCheck, TriangleAlert } from 'lucide-react'
import { Cell } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

import { ForYouChatsList } from '@renderer/overlay/Sidepanel/components/sidepanel/sections/running-chats'
import { StreamingChatIndicator } from '@renderer/components/streaming-chat-indicator'
import { SIDEPANEL_CELL_SURFACE } from '@renderer/overlay/Sidepanel/components/cells/surface'
import { useForYouDisplayChats } from '@renderer/overlay/hooks/for-you-linger'
import type { ForYouChatsResult } from '@renderer/overlay/hooks/use-running-chats'
import { ReminderCell } from '../../../components/reminders/reminders-cell'
import { UnreadBadge } from '../../../components/unread-badge'
import { resolveFollowUpsEmptyCopy } from '../meeting-follow-ups'
import type { MeetingFollowUpsResult } from '../use-meeting-follow-ups'
import type { MeetingSummaryPhase } from '../derive-meeting-summary-state'
import { MeetingViewSection } from './section'

const STATUS_LEADING = 'flex size-5 shrink-0 items-center justify-center'
const STATUS_TITLE = 'text-foreground type-ui-sm min-w-0 font-normal'

interface MeetingViewForYouProps {
  chats: ForYouChatsResult
  followUps: MeetingFollowUpsResult
  phase: MeetingSummaryPhase
  canDraftFollowUps: boolean
  isPlannerRunning: boolean
  /** `todo` draws chats and reminders as to-dos, reminders opening their popover, the way the To-dos Home does. */
  followUpAppearance: MeetingFollowUpAppearance
}

export type MeetingFollowUpAppearance = 'row' | 'todo'

interface ForYouStatusItemProps {
  icon: ReactNode
  children: ReactNode
}

function ForYouStatusItem({ icon, children }: ForYouStatusItemProps) {
  return (
    <Cell
      size="compact"
      clickable={false}
      tabIndex={-1}
      className={twMerge(SIDEPANEL_CELL_SURFACE, 'pointer-events-none flex-col px-3 py-2')}
    >
      <div className="flex w-full items-start gap-2">
        {icon}
        {children}
      </div>
    </Cell>
  )
}

function ForYouProcessingItem({ children }: { children: string }) {
  return (
    <ForYouStatusItem
      icon={
        <span className={STATUS_LEADING}>
          <StreamingChatIndicator label={children} size={16} tone="dark-orange" />
        </span>
      }
    >
      <span className={twMerge(STATUS_TITLE, 'text-orange-11 flex-1')}>{children}</span>
    </ForYouStatusItem>
  )
}

function ForYouEmptyItem({ description, phase }: { description: string; phase: MeetingSummaryPhase }) {
  const hasSummaryProblem = phase === 'failed' || phase === 'missing'
  const title =
    phase === 'failed'
      ? 'Summary generation failed'
      : phase === 'missing'
        ? 'Summary unavailable'
        : 'No follow-ups found'

  return (
    <ForYouStatusItem
      icon={
        <span className={twMerge(STATUS_LEADING, 'text-foreground-secondary [&_svg]:size-4')}>
          {hasSummaryProblem ? <TriangleAlert aria-hidden="true" /> : <SearchCheck aria-hidden="true" />}
        </span>
      }
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={twMerge(STATUS_TITLE, hasSummaryProblem && 'text-danger-subtle')}>{title}</span>
        <span className="text-foreground-secondary type-ui-xs">{description}</span>
      </div>
    </ForYouStatusItem>
  )
}

export function MeetingViewForYou({
  chats,
  followUps,
  phase,
  canDraftFollowUps,
  isPlannerRunning,
  followUpAppearance,
}: MeetingViewForYouProps) {
  const reminders = followUps.items
  // Archiving invalidates every For You query, this meeting's included, so the row has to be
  // held here through its undo window like it is anywhere else.
  const displayChats = useForYouDisplayChats(chats.data, chats.feedKey)
  const hasContent = displayChats.length > 0 || reminders.length > 0
  const isSettled = !chats.isLoading && followUps.isSettled
  const isGeneratingEmptyState = phase === 'preparing' || phase === 'streaming'
  const emptyCopy =
    !hasContent && !isPlannerRunning && (isSettled || isGeneratingEmptyState) && !chats.isError
      ? resolveFollowUpsEmptyCopy(phase, canDraftFollowUps)
      : undefined

  if (!hasContent && !isPlannerRunning && !emptyCopy && !chats.isError) {
    return null
  }

  const openCount = displayChats.length + reminders.filter((item) => item.isOpen).length
  const isTodo = followUpAppearance === 'todo'
  const reminderRows = reminders.map((item) => (
    <ReminderCell
      key={item.id}
      item={item.reminder}
      variant="row"
      mark={isTodo ? 'todo' : undefined}
      opensIn={isTodo ? 'popover' : 'row'}
      retainsResolved
    />
  ))

  return (
    // The tour anchor rides the content, not the section. The shell also
    // renders to carry the empty copy and the load error, and a step reading
    // "follow-ups, drafted for you" pointed at either of those is worse than
    // no step at all — the tour drops a step whose target it can't find.
    <MeetingViewSection
      icon={<ListTodo />}
      title="For You"
      badge={<UnreadBadge count={openCount} />}
      expanded
      tourId={hasContent ? 'meeting-follow-ups' : undefined}
    >
      {chats.isError && (
        <p className="text-danger-subtle type-ui-sm px-3 py-1">Couldn’t load For You. Try refreshing.</p>
      )}
      {(isPlannerRunning || emptyCopy || hasContent) && (
        <div className="flex flex-col gap-[2px]">
          {isPlannerRunning && (
            <ForYouProcessingItem>
              {hasContent
                ? 'Processing meeting. More follow-ups may appear here.'
                : 'Processing meeting. Your follow-ups will be drafted here.'}
            </ForYouProcessingItem>
          )}
          {/* Always mounted, with the empty item in its slot, so the last row exits into it. */}
          <ForYouChatsList
            chats={displayChats}
            sourceLabel="Meeting"
            feedKey={chats.feedKey}
            appearance={isTodo ? 'todo' : 'card'}
            afterInProgress={reminderRows}
            empty={
              emptyCopy &&
              (isGeneratingEmptyState ? (
                <ForYouProcessingItem>{emptyCopy}</ForYouProcessingItem>
              ) : (
                <ForYouEmptyItem description={emptyCopy} phase={phase} />
              ))
            }
          />
        </div>
      )}
    </MeetingViewSection>
  )
}
